/**
 * A pooled, *ordered* row of kit AuraIcons (real icons, duration sweep, time, stacks, dispel
 * border, describeAura tooltips). Ordering comes from model.ts `orderAuras`; the scratch list
 * and the icons are reused every frame.
 */
import type { AuraInstance, ContentRegistry, DeepReadonly, DispelType, Unit, UnitId, World } from '@keystone/sim';
import { el } from '../dom';
import { AuraIcon, type AuraIconSize } from '../kit';
import { type TooltipLive, auraTooltipModel } from '../tooltip/hud';
import { type AuraBucket, orderAuras } from './model';

export interface FrameAuraRowOptions {
  kind: AuraBucket;
  max: number;
  size?: AuraIconSize;
  /** Nameplate style: only the viewer's auras plus CC. */
  mineOnly?: boolean;
  className?: string;
}

export class FrameAuraRow {
  readonly root: HTMLElement;
  private readonly icons: AuraIcon[] = [];
  private readonly list: DeepReadonly<AuraInstance>[] = [];
  private readonly opts: { kind: AuraBucket; playerId: UnitId | null; dispel: ReadonlySet<DispelType> | null; mineOnly: boolean; max: number };
  private readonly size: AuraIconSize;
  private readonly live: TooltipLive & { unitId: UnitId | null } = { world: null as never, content: null as never, playerId: null, unitId: null };
  /** Icons shown last frame. */
  count = 0;

  constructor(parent: HTMLElement | null, o: FrameAuraRowOptions) {
    this.root = el('div', `hk-auras fr-auras ${o.kind}${o.className ? ` ${o.className}` : ''}`);
    this.size = o.size ?? 'xs';
    this.opts = { kind: o.kind, playerId: null, dispel: null, mineOnly: !!o.mineOnly, max: o.max };
    parent?.appendChild(this.root);
  }

  /** `now`: hudNow (sim-time present). `dispel`: the viewer's dispel types (sorts those first). */
  update(world: DeepReadonly<World>, content: ContentRegistry, unit: DeepReadonly<Unit> | undefined, playerId: UnitId | null, now: number, dispel: ReadonlySet<DispelType> | null = null): number {
    this.live.world = world;
    this.live.content = content;
    this.live.playerId = playerId;
    this.live.unitId = unit?.id ?? null;
    this.opts.playerId = playerId;
    this.opts.dispel = dispel;
    const n = unit ? orderAuras(unit.auras, content, now, this.opts, this.list) : 0;
    for (let i = 0; i < n; i++) {
      const inst = this.list[i]!;
      this.icon(i).set(inst, content.aura(inst.auraId), now, content);
    }
    for (let i = n; i < this.icons.length; i++) this.icons[i]!.show(false);
    this.list.length = 0;
    this.count = n;
    return n;
  }

  private icon(i: number): AuraIcon {
    let ic = this.icons[i];
    if (!ic) {
      const box: { icon: AuraIcon | null } = { icon: null };
      ic = new AuraIcon(this.root, this.size, () => auraTooltipModel(this.live.world ? this.live : null, this.live.unitId, box.icon?.iid));
      box.icon = ic;
      this.icons.push(ic);
    }
    return ic;
  }
}
